/**
 * Monthly order cap: empty/null means unlimited. Integer >= 0 is a hard cap.
 * Backend maps '' and null to unlimited. Omitting the key on update keeps
 * the stored value, so edits that clear the field must still send ''.
 */
export function toMonthlyLimit(value: string): number | "" {
  return value.trim() === "" ? "" : Number(value);
}

export function resolveMonthlyLimitPayload(value: unknown): number | "" {
  if (value === undefined || value === null) {
    return "";
  }
  return toMonthlyLimit(String(value));
}

export function isValidMonthlyLimit(value: unknown): boolean {
  if (value === undefined || value === null || value === "") {
    return true;
  }
  const monthlyLimit = resolveMonthlyLimitPayload(value);
  return monthlyLimit === "" || (Number.isInteger(monthlyLimit) && monthlyLimit >= 0);
}

export const isAwfarAdminUser = (user: { type?: unknown } | null | undefined): boolean =>
  Number(user?.type) === 0;

const toErrorMessages = (value: unknown): string[] => {
  const list = Array.isArray(value) ? value : [value];
  return list.filter((msg): msg is string => typeof msg === "string" && msg.trim() !== "");
};

/**
 * Surface API 422 `errors.order_monthly_limit` under the matching form input.
 * Returns true when a field error was applied.
 */
export function applyOrderMonthlyLimitApiError(
  form: { setFields: (fields: { name: any; errors: string[] }[]) => void },
  error: unknown,
  fieldName: string | (string | number)[] = "order_monthly_limit"
): boolean {
  const data = (error as any)?.response?.data;
  const bags = [data?.errors, data?.message].filter(
    (bag) => bag && typeof bag === "object" && !Array.isArray(bag)
  );

  for (const bag of bags) {
    const messages = toErrorMessages(bag.order_monthly_limit);
    if (messages.length > 0) {
      form.setFields([{ name: fieldName, errors: messages }]);
      return true;
    }
  }

  return false;
}

export type OrderMonthlyLimitStatus = {
  reached: boolean;
  limit: number | null;
  used: number;
};

/**
 * Read order_monthly_limit_status from GET /api/v1/company (next to setup_status).
 * reached = used >= limit when a limit is set; always false when unlimited (limit null).
 */
export const getOrderMonthlyLimitStatus = (
  company: any
): OrderMonthlyLimitStatus | null => {
  const status =
    company?.order_monthly_limit_status ??
    company?.company_information?.order_monthly_limit_status ??
    company?.main_settings?.basic_info?.order_monthly_limit_status ??
    null;

  if (!status || typeof status !== "object") {
    return null;
  }

  return {
    reached: status.reached === true,
    limit:
      status.limit === undefined || status.limit === null
        ? null
        : Number(status.limit),
    used: Number(status.used ?? 0),
  };
};
